import type { Identifier } from "ra-core";
import type {
  TaskRecurrenceIntervalType,
  TaskRecurrenceIntervalUnit,
} from "../types";

export type TaskRecurrenceFormValues = {
  recurring_enabled?: boolean;
  recurrence_interval_type?: TaskRecurrenceIntervalType;
  recurrence_interval_count?: number;
  recurrence_interval_unit?: TaskRecurrenceIntervalUnit;
  recurrence_has_end?: boolean;
  recurrence_ends_at?: string | null;
};

export type CreateTaskPayload = {
  contact_id: Identifier;
  type?: string;
  text?: string;
  due_date?: string;
  done_date?: string | null;
  sales_id?: Identifier;
  recurrence_id?: Identifier | null;
} & TaskRecurrenceFormValues;

const UNIT_TO_MS: Record<TaskRecurrenceIntervalUnit, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
  year: 365 * 24 * 60 * 60 * 1000,
};

export const getRecurrenceUnit = (
  intervalType?: TaskRecurrenceIntervalType,
  intervalUnit?: TaskRecurrenceIntervalUnit,
): TaskRecurrenceIntervalUnit => {
  if (intervalType === "custom") {
    return intervalUnit ?? "day";
  }
  if (intervalType === "week") return "week";
  if (intervalType === "month") return "month";
  if (intervalType === "year") return "year";
  return "day";
};

export const normalizeRecurringTaskPayload = (data: CreateTaskPayload) => {
  const recurringEnabled = !!data.recurring_enabled;
  const intervalType = data.recurrence_interval_type ?? "day";
  const intervalCount = Math.max(1, Number(data.recurrence_interval_count ?? 1));
  const intervalUnit = getRecurrenceUnit(intervalType, data.recurrence_interval_unit);
  const endsAt =
    recurringEnabled && data.recurrence_has_end ? (data.recurrence_ends_at ?? null) : null;

  return {
    baseTask: {
      contact_id: data.contact_id,
      type: data.type ?? "none",
      text: data.text ?? "",
      due_date: data.due_date ?? new Date().toISOString(),
      done_date: data.done_date ?? null,
      sales_id: data.sales_id,
      recurrence_id: data.recurrence_id ?? null,
    },
    recurrence: recurringEnabled
      ? {
          enabled: true as const,
          intervalType,
          intervalCount,
          intervalUnit,
          endsAt,
        }
      : {
          enabled: false as const,
          intervalType,
          intervalCount,
          intervalUnit,
          endsAt: null,
        },
  };
};

export const calculateNextRecurringDueDate = (options: {
  completedAt: string;
  intervalType: TaskRecurrenceIntervalType;
  intervalCount: number;
  intervalUnit: TaskRecurrenceIntervalUnit;
}) => {
  const completedAt = new Date(options.completedAt);
  const count = Math.max(1, options.intervalCount);
  const unit = getRecurrenceUnit(options.intervalType, options.intervalUnit);

  if (unit === "month") {
    const next = new Date(completedAt);
    next.setMonth(next.getMonth() + count);
    return next.toISOString();
  }
  if (unit === "year") {
    const next = new Date(completedAt);
    next.setFullYear(next.getFullYear() + count);
    return next.toISOString();
  }

  return new Date(completedAt.getTime() + count * UNIT_TO_MS[unit]).toISOString();
};
